import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

function CategoryDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        className="flex items-center gap-1.5 text-sm font-medium text-white/90 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        Categories
        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-3 w-56 bg-white rounded-xl py-2 z-50">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setIsOpen(false)}
            >
              {cat.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`text-xl font-heading font-bold whitespace-nowrap cursor-pointer ${
              scrolled ? "text-foreground-900" : "text-white"
            }`}
          >
            GTBuy<span className={scrolled ? "text-primary-500" : "text-white/80"}>.Garden</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/80 hover:text-white"
              }`}
            >
              Home
            </Link>
            <CategoryDropdown />
            <Link
              to="/article/gtbuy-spreadsheet-complete-guide"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/80 hover:text-white"
              }`}
            >
              Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/80 hover:text-white"
              }`}
            >
              Main Store
            </a>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                scrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
              }`}
            >
              Buy It Now
            </a>

            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
                scrolled ? "text-foreground-800" : "text-white"
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto z-40">
          <div className="px-6 py-4">
            <Link
              to="/"
              className="block py-3 text-base font-medium text-foreground-800 hover:text-primary-500 cursor-pointer border-b border-background-200"
              onClick={() => setMobileOpen(false)}
            >
              Home
            </Link>
            <div className="py-3 border-b border-background-200">
              <p className="text-sm font-semibold text-foreground-500 mb-3">Categories</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block py-2 text-sm text-foreground-700 hover:text-primary-500 cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
            <Link
              to="/article/gtbuy-spreadsheet-complete-guide"
              className="block py-3 text-base font-medium text-foreground-800 hover:text-primary-500 cursor-pointer border-b border-background-200"
              onClick={() => setMobileOpen(false)}
            >
              Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block py-3 text-base font-medium text-foreground-800 hover:text-primary-500 cursor-pointer border-b border-background-200"
            >
              Main Store
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center mt-5 px-6 py-3 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}